import { Injectable } from "@angular/core";
import { AuthService } from "@core/auth.service";
import { UtilityService } from "@core/utility.service";
import { VERSION } from "../../environments/version";
import { CommonVariable } from "../app.constants";

declare let analytics: any;
declare var window: any;

type LaunchMode = "standalone" | "browser";

/**
 * Canonical PWA launch / install attribution.
 *
 * Distinguishes three things the business needs to keep separate:
 *  - a true install event ("App Installed", Chromium `appinstalled` only)
 *  - an installed-app launch ("App Launched" with launch_mode = "standalone")
 *  - a normal browser visit ("App Launched" with launch_mode = "browser")
 *
 * All signals are emitted through Segment (window.analytics) so Mixpanel and
 * CleverTap receive the same payload. This is additive: the legacy
 * `?mode=standalone` URL marker and the existing "Page Viewed" event are left
 * in place so current BA reporting is unaffected.
 *
 * Platform caveat: `appinstalled` is Chromium-only. iOS/Safari never fires it,
 * so a true-install count is not available cross-browser. iOS installs are only
 * observable as installed-app launches via navigator.standalone.
 */
@Injectable({ providedIn: "root" })
export class PwaLaunchTrackingService {
  private readonly EVENT_APP_LAUNCHED = "App Launched";
  private readonly EVENT_APP_INSTALLED = "App Installed";
  private readonly FIRST_STANDALONE_LAUNCH_KEY = "ls_pwa_first_standalone_launch_done";

  private launchTracked = false;
  private installListenerBound = false;
  private resolvedPlatform = "";

  constructor(private authService: AuthService) {}

  /**
   * Entry point called once from the app shell at bootstrap.
   */
  init(): void {
    this.resolvePlatform();
    this.trackLaunch();
    this.registerInstallListener();
  }

  /**
   * Resolve the device OS synchronously, before any event is emitted.
   *
   * CommonVariable.device is populated asynchronously during bootstrap via
   * Device.getInfo(), so it is frequently still empty when the launch event
   * fires — that is why "App Launched" was sending platform = "". We prefer the
   * shared value when it is already available, otherwise derive the OS from the
   * user agent immediately. This keeps event emission synchronous (no risk of a
   * slow/never-resolving promise delaying or dropping the launch event) and
   * guarantees platform is never empty on web.
   */
  private resolvePlatform(): void {
    const shared = CommonVariable?.device?.operatingSystem;
    this.resolvedPlatform = shared || this.detectPlatformFromUserAgent();
  }

  /**
   * Best-effort synchronous OS detection from the user agent. Returns values
   * aligned with Capacitor's Device.operatingSystem ("ios" | "android" |
   * "windows" | "mac" | "unknown").
   *
   * iPadOS 13+ reports a desktop Macintosh UA; detect those via maxTouchPoints
   * so they are not misclassified as "mac".
   */
  private detectPlatformFromUserAgent(): string {
    try {
      const ua = (window.navigator?.userAgent || "").toLowerCase();
      const maxTouchPoints = window.navigator?.maxTouchPoints || 0;
      // iPadOS 13+ spoofs Macintosh in the UA but still exposes multi-touch.
      if (
        /iphone|ipad|ipod/.test(ua) ||
        (/macintosh|mac os x/.test(ua) && maxTouchPoints > 1)
      ) {
        return "ios";
      }
      if (/android/.test(ua)) return "android";
      if (/windows/.test(ua)) return "windows";
      if (/macintosh|mac os x/.test(ua)) return "mac";
      return "unknown";
    } catch {
      return "unknown";
    }
  }

  /**
   * Emit a single canonical "App Launched" event describing how this session
   * was opened. Fired once per app load.
   */
  trackLaunch(): void {
    if (this.launchTracked) return;
    this.launchTracked = true;

    try {
      const isStandalone = UtilityService.isAppInstalledOnHomePage();
      const launchMode: LaunchMode = isStandalone ? "standalone" : "browser";
      const isFirstStandaloneLaunch = isStandalone && this.markFirstStandaloneLaunch();

      const payload = {
        launch_mode: launchMode,
        display_mode: this.getDisplayMode(),
        is_ios_standalone: this.isIosStandalone(),
        is_first_standalone_launch: isFirstStandaloneLaunch,
        platform: this.resolvedPlatform,
        app_version: VERSION,
      };

      this.track(this.EVENT_APP_LAUNCHED, payload);

      // Always refresh last_launch_mode so a later browser open does not leave
      // a stale "standalone" trait. installed_app_user is set only for
      // standalone launches (and on Chromium appinstalled below).
      this.setLaunchTraits(launchMode);
    } catch (error) {
      this.authService.errorHandlerLogs("[PwaLaunchTracking] trackLaunch error", error);
    }
  }

  /**
   * Register the Chromium `appinstalled` listener to capture the true-install
   * signal. Safe no-op on browsers (iOS/Safari) that never fire the event.
   */
  registerInstallListener(): void {
    if (this.installListenerBound || typeof window === "undefined") return;
    this.installListenerBound = true;

    try {
      window.addEventListener("appinstalled", () => {
        try {
          this.track(this.EVENT_APP_INSTALLED, {
            display_mode: this.getDisplayMode(),
            platform: CommonVariable?.device?.operatingSystem || this.resolvedPlatform,
            app_version: VERSION,
          });
          this.setLaunchTraits("standalone");
        } catch (error) {
          this.authService.errorHandlerLogs("[PwaLaunchTracking] appinstalled handler error", error);
        }
      });
    } catch (error) {
      this.authService.errorHandlerLogs("[PwaLaunchTracking] registerInstallListener error", error);
    }
  }

  /**
   * Returns the resolved PWA display mode. Covers the standard display-mode
   * media features plus the iOS navigator.standalone fallback.
   */
  private getDisplayMode(): string {
    try {
      if (this.isIosStandalone()) return "standalone";
      const modes = ["fullscreen", "standalone", "minimal-ui"];
      for (const mode of modes) {
        if (window.matchMedia?.(`(display-mode: ${mode})`)?.matches) {
          return mode;
        }
      }
    } catch {
      // matchMedia unavailable — fall through to browser.
    }
    return "browser";
  }

  private isIosStandalone(): boolean {
    try {
      return window.navigator?.standalone === true;
    } catch {
      return false;
    }
  }

  /**
   * Records the first-ever standalone launch on this device.
   * Returns true only the first time it is seen so repeated standalone
   * launches are not counted as first launches.
   */
  private markFirstStandaloneLaunch(): boolean {
    try {
      if (window.localStorage?.getItem(this.FIRST_STANDALONE_LAUNCH_KEY)) {
        return false;
      }
      window.localStorage?.setItem(this.FIRST_STANDALONE_LAUNCH_KEY, new Date().toISOString());
      return true;
    } catch {
      return false;
    }
  }

  private setLaunchTraits(launchMode: LaunchMode): void {
    try {
      if (typeof analytics === "undefined" || !analytics?.identify) return;
      const traits: Record<string, unknown> = {
        last_launch_mode: launchMode,
      };
      if (launchMode === "standalone") {
        traits.installed_app_user = true;
      }
      analytics.identify(traits);
    } catch (error) {
      this.authService.errorHandlerLogs("[PwaLaunchTracking] setLaunchTraits error", error);
    }
  }

  private track(event: string, payload: Record<string, unknown>): void {
    try {
      if (typeof analytics === "undefined" || !analytics?.track) return;
      analytics.track(event, payload);
    } catch (error) {
      this.authService.errorHandlerLogs(`[PwaLaunchTracking] track "${event}" error`, error);
    }
  }
}
